import { useCallback, useEffect, useRef, useState } from 'react';
import { useVerifySmartLinkSlugMutation } from '../../../redux/api/fymAPI';
import { titleSlugSuggestion } from './titleSlugSuggestion';

export enum SlugState {
  /** Nothing typed yet, or nothing to check. */
  idle = 'idle',
  checking = 'checking',
  free = 'free',
  /** Somebody's published page already has it. */
  taken = 'taken',
  /** Characters the address cannot carry. */
  illegal = 'illegal',
}

export interface CreationSlug {
  value: string;
  state: SlugState;
  /** What the server would give instead, when this one will not do. */
  suggestion?: string;
  setValue: (value: string) => void;
  /** Take the server's suggestion. */
  useSuggestion: () => void;
  /** Mark the current answer as taken, after the create call said so. */
  markTaken: (suggestion?: string) => void;
}

const CHECK_DELAY_MS = 450;

/**
 * The page address, checked while it is typed.
 *
 * It follows the title until the artist touches it, which is what makes the
 * field feel like a suggestion rather than a chore. `slugify` is the server's
 * own, so what is checked is exactly what will be stored.
 */
export function useCreationSlug(accountId: number, suggestedFrom: string | undefined): CreationSlug {
  const [verifySlug] = useVerifySmartLinkSlugMutation();
  const [value, setRawValue] = useState('');
  const [state, setState] = useState(SlugState.idle);
  const [suggestion, setSuggestion] = useState<string>();
  const isTouched = useRef(false);

  useEffect(() => {
    if (isTouched.current || suggestedFrom === undefined || suggestedFrom === '') {
      return;
    }
    setRawValue(titleSlugSuggestion(suggestedFrom));
  }, [suggestedFrom]);

  const setValue = useCallback((next: string) => {
    isTouched.current = true;
    setRawValue(next);
  }, []);

  const useSuggestion = useCallback(() => {
    if (suggestion === undefined) {
      return;
    }
    isTouched.current = true;
    setRawValue(suggestion);
  }, [suggestion]);

  const markTaken = useCallback((nextSuggestion?: string) => {
    setState(SlugState.taken);
    setSuggestion(nextSuggestion);
  }, []);

  useEffect(() => {
    if (value === '') {
      setState(SlugState.idle);
      setSuggestion(undefined);
      return undefined;
    }
    setState(SlugState.checking);
    const timer = setTimeout(() => {
      verifySlug({ accountId, slug: value })
        .unwrap()
        .then((result) => {
          setSuggestion(result.availableSlug);
          if (!result.isValid) {
            setState(SlugState.illegal);
            return;
          }
          setState(result.isAvailable ? SlugState.free : SlugState.taken);
        })
        // A check we could not make is not a verdict. The create call checks
        // again anyway, and answers 409 if this one was wrong.
        .catch(() => setState(SlugState.idle));
    }, CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [accountId, value, verifySlug]);

  return { value, state, suggestion, setValue, useSuggestion, markTaken };
}
